import { useEffect, useMemo, useState } from "react";
import api, { formatMoney, formatDate } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle, Warning, CaretLeft, CaretRight, FileArrowDown, Plus, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import MoneyInput from "@/components/MoneyInput";
import IbanInput from "@/components/IbanInput";

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (m) => {
  const [y, mm] = m.split("-");
  return new Date(y, mm - 1, 1).toLocaleDateString("tr-TR", { year: "numeric", month: "long" });
};

const CopyBtn = ({ value, label, testid }) => {
  if (!value) return <span className="text-muted-foreground text-xs">-</span>;
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} kopyalandı`);
    } catch {
      toast.error("Kopyalama başarısız");
    }
  };
  return (
    <button onClick={copy} data-testid={testid}
      className="inline-flex items-center gap-1 group hover:bg-secondary/50 rounded px-1 py-0.5 -mx-1 transition"
      title={`${label} kopyala`}>
      <span className="tabular-nums text-xs">{value}</span>
      <Copy size={12} className="opacity-40 group-hover:opacity-100" />
    </button>
  );
};

export default function CalendarPage() {
  const { companies, selectedCompany } = useCompany();
  const [tab, setTab] = useState("plan");
  const [selectedMonth, setSelectedMonth] = useState(monthKey(new Date()));
  const [plan, setPlan] = useState(null);
  const [windows, setWindows] = useState(null);
  const [addDialog, setAddDialog] = useState(false);
  const [newPay, setNewPay] = useState({
    company_id: "", baslik: "", ticari_unvan: "", odeme_sekli: "Havale/EFT",
    banka: "", iban: "", tutar: 0, currency: "TRY", vade_tarihi: "",
  });

  const cid = selectedCompany === "all" ? "" : selectedCompany;

  const loadPlan = async () => {
    const q = `?month=${selectedMonth}${cid ? `&company_id=${cid}` : ""}`;
    const { data } = await api.get(`/dashboard/payment-plan${q}`);
    setPlan(data);
  };
  const loadWindows = async () => {
    const q = cid ? `?company_id=${cid}&weeks=16` : "?weeks=16";
    const { data } = await api.get(`/dashboard/check-writing-windows${q}`);
    setWindows(data);
  };

  useEffect(() => { loadPlan(); /* eslint-disable-next-line */ }, [selectedMonth, selectedCompany]);
  useEffect(() => { loadWindows(); /* eslint-disable-next-line */ }, [selectedCompany]);

  const shiftMonth = (delta) => {
    const [y, m] = selectedMonth.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setSelectedMonth(monthKey(d));
  };

  const openAddPayment = () => {
    const [y, m] = selectedMonth.split("-").map(Number);
    const defaultDate = `${y}-${String(m).padStart(2, "0")}-15`;
    setNewPay({
      company_id: selectedCompany !== "all" ? selectedCompany : companies[0]?.id || "",
      baslik: "", ticari_unvan: "", odeme_sekli: "Havale/EFT",
      banka: "", iban: "", tutar: 0, currency: "TRY", vade_tarihi: defaultDate,
    });
    setAddDialog(true);
  };

  const savePayment = async () => {
    if (!newPay.company_id) { toast.error("Şirket seçin"); return; }
    if (!newPay.baslik) { toast.error("Açıklama girin"); return; }
    if (!newPay.vade_tarihi) { toast.error("Vade tarihi girin"); return; }
    if (newPay.tutar <= 0) { toast.error("Tutar 0'dan büyük olmalı"); return; }
    try {
      const payload = {
        company_id: newPay.company_id,
        tip: "Borç",
        proje: "",
        cari_unvan: newPay.ticari_unvan || newPay.baslik,
        hizmet_cinsi: newPay.baslik,
        anlasma_tutari: newPay.tutar,
        vade_sayisi: 1,
        taksitler: [],
        baslik: newPay.baslik,
        kategori: "",
        tutar: newPay.tutar,
        currency: newPay.currency,
        vade_tarihi: newPay.vade_tarihi,
        durum: "Beklemede",
        odeme_sekli: newPay.odeme_sekli,
        ticari_unvan: newPay.ticari_unvan,
        banka: newPay.banka,
        iban: newPay.iban,
      };
      await api.post("/transactions", payload);
      toast.success("Ödeme planına eklendi");
      setAddDialog(false);
      loadPlan();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(x => x.msg).join(", ") : (detail || "Kayıt hatası");
      toast.error(msg);
    }
  };


  const exportPlanPDF = async () => {
    if (!plan) return;
    const { jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text(`Aylik Odeme Plani - ${monthLabel(selectedMonth)}`, 14, 15);
    doc.setFontSize(9);
    doc.text(`Toplam: ${formatMoney(plan.total_try)}`, 14, 21);
    autoTable(doc, {
      startY: 25,
      head: [["Sira", "Vade", "Firma/Taseron", "Odeme Sekli", "Aciklama", "Banka", "IBAN", "Tutar"]],
      body: plan.rows.map((r) => [
        r.sira, formatDate(r.vade_tarihi), r.firma, r.odeme_sekli,
        r.aciklama, r.banka, r.iban, formatMoney(r.tutar, r.currency),
      ]),
      styles: { fontSize: 8 },
    });
    doc.save(`odeme-plani-${selectedMonth}.pdf`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Ödeme Takvimi</h1>
        <p className="text-sm text-muted-foreground mt-1">Aylık ödeme planınız ve çek yazma uygunluk pencereleri.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="plan" data-testid="tab-plan">Aylık Ödeme Planı</TabsTrigger>
          <TabsTrigger value="check" data-testid="tab-check">Çek Yazma Uygunluğu</TabsTrigger>
        </TabsList>

        <TabsContent value="plan" className="space-y-4 mt-6">
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <Button variant="outline" size="icon" onClick={() => shiftMonth(-1)} data-testid="prev-month"><CaretLeft size={16} /></Button>
                <CardTitle className="font-heading text-xl min-w-48 text-center">{monthLabel(selectedMonth)}</CardTitle>
                <Button variant="outline" size="icon" onClick={() => shiftMonth(1)} data-testid="next-month"><CaretRight size={16} /></Button>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Toplam (TRY)</div>
                  <div className="font-heading font-extrabold text-xl tabular-nums">
                    {plan ? formatMoney(plan.total_try) : "-"}
                  </div>
                </div>
                <Button variant="outline" onClick={exportPlanPDF} data-testid="plan-pdf-btn">
                  <FileArrowDown size={16} className="mr-2" /> PDF
                </Button>
                <Button onClick={openAddPayment} data-testid="add-payment-btn">
                  <Plus size={16} className="mr-2" /> Yeni Ödeme Ekle
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 space-y-6">
              {plan && plan.rows.length === 0 && (
                <div className="text-center text-muted-foreground py-8">Bu ay için ödeme bulunmuyor.</div>
              )}
              {plan && plan.rows.length > 0 && (() => {
                const cek_rows = plan.rows.filter((r) => r.odeme_sekli === "Çek");
                const nakit_rows = plan.rows.filter((r) => r.odeme_sekli !== "Çek");
                const cek_total = cek_rows.filter((r) => r.currency === "TRY").reduce((a, b) => a + b.tutar, 0);
                const nakit_total = nakit_rows.filter((r) => r.currency === "TRY").reduce((a, b) => a + b.tutar, 0);
                const renderTable = (rows, testidPrefix) => (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Sıra</TableHead>
                        <TableHead>Vade</TableHead>
                        <TableHead>Firma / Taşeron</TableHead>
                        <TableHead>Ödeme Şekli</TableHead>
                        <TableHead>Açıklama</TableHead>
                        <TableHead>Banka</TableHead>
                        <TableHead>IBAN</TableHead>
                        <TableHead className="text-right">Tutar</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow key={r.sira} data-testid={`${testidPrefix}-row-${r.sira}`}>
                          <TableCell className="tabular-nums">{r.sira}</TableCell>
                          <TableCell className="tabular-nums text-sm">{formatDate(r.vade_tarihi)}</TableCell>
                          <TableCell className="font-medium text-sm">{r.firma}</TableCell>
                          <TableCell>
                            <Badge className={
                              r.odeme_sekli === "Çek" ? "badge-purple" :
                              r.odeme_sekli === "Nakit" ? "badge-yellow" : "badge-blue"
                            }>{r.odeme_sekli}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{r.aciklama}</TableCell>
                          <TableCell className="text-sm">
                            <CopyBtn value={r.banka} label="Banka" testid={`copy-banka-${r.sira}`} />
                            {r.sube && <div className="text-xs text-muted-foreground mt-0.5">{r.sube}</div>}
                          </TableCell>
                          <TableCell>
                            <CopyBtn value={r.iban} label="IBAN" testid={`copy-iban-${r.sira}`} />
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-medium">{formatMoney(r.tutar, r.currency)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                );
                return (
                  <>
                    {cek_rows.length > 0 && (
                      <div data-testid="plan-cek-section">
                        <div className="px-6 pt-4 pb-2 flex items-center justify-between border-t border-border">
                          <div>
                            <h3 className="font-heading font-bold text-base">Çekler</h3>
                            <p className="text-xs text-muted-foreground">Bu ay ödenecek kendi çekleriniz</p>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-muted-foreground">Çek Toplamı (TRY)</div>
                            <div className="font-heading font-bold tabular-nums" data-testid="cek-total">{formatMoney(cek_total)}</div>
                            <div className="text-xs text-muted-foreground">{cek_rows.length} adet</div>
                          </div>
                        </div>
                        {renderTable(cek_rows, "cek")}
                      </div>
                    )}
                    {nakit_rows.length > 0 && (
                      <div data-testid="plan-nakit-section">
                        <div className="px-6 pt-4 pb-2 flex items-center justify-between border-t border-border">
                          <div>
                            <h3 className="font-heading font-bold text-base">Nakit / Havale / EFT</h3>
                            <p className="text-xs text-muted-foreground">Havale, EFT, nakit ve diğer ödemeler</p>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-muted-foreground">Nakit Toplamı (TRY)</div>
                            <div className="font-heading font-bold tabular-nums" data-testid="nakit-total">{formatMoney(nakit_total)}</div>
                            <div className="text-xs text-muted-foreground">{nakit_rows.length} adet</div>
                          </div>
                        </div>
                        {renderTable(nakit_rows, "nakit")}
                      </div>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="check" className="space-y-4 mt-6">
          {windows && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-border"><CardContent className="p-6">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Başlangıç Bakiyesi</div>
                  <div className="font-heading font-extrabold text-2xl tabular-nums mt-1">{formatMoney(windows.starting_balance)}</div>
                </CardContent></Card>
                <Card className="border-border"><CardContent className="p-6">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Uygun Hafta</div>
                  <div className="font-heading font-extrabold text-2xl tabular-nums mt-1 urgency-green">{windows.windows.filter((w) => w.uygun).length}</div>
                </CardContent></Card>
                <Card className="border-border"><CardContent className="p-6">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Max Tek Hafta</div>
                  <div className="font-heading font-extrabold text-2xl tabular-nums mt-1 urgency-green">
                    {formatMoney(Math.max(0, ...windows.windows.map((w) => w.yazilabilir_tutar)))}
                  </div>
                </CardContent></Card>
              </div>

              <Card className="border-border">
                <CardHeader><CardTitle className="font-heading">16 Haftalık Çek Yazma Pencereleri</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Hafta</TableHead>
                        <TableHead>Tarih Aralığı</TableHead>
                        <TableHead className="text-right">Giren</TableHead>
                        <TableHead className="text-right">Çıkan</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                        <TableHead className="text-right">Kümülatif</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead className="text-right">Yazılabilir Tutar</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {windows.windows.map((w) => (
                        <TableRow key={w.week_no} data-testid={`window-${w.week_no}`}>
                          <TableCell className="tabular-nums">{w.week_no}</TableCell>
                          <TableCell className="text-sm tabular-nums">{formatDate(w.baslangic)} – {formatDate(w.bitis)}</TableCell>
                          <TableCell className="text-right tabular-nums urgency-green text-sm">+{formatMoney(w.giren)}</TableCell>
                          <TableCell className="text-right tabular-nums urgency-red text-sm">-{formatMoney(w.cikan)}</TableCell>
                          <TableCell className={`text-right tabular-nums text-sm ${w.net >= 0 ? "urgency-green" : "urgency-red"}`}>{formatMoney(w.net)}</TableCell>
                          <TableCell className={`text-right tabular-nums text-sm ${w.kumulatif_bitis >= 0 ? "" : "urgency-red font-semibold"}`}>{formatMoney(w.kumulatif_bitis)}</TableCell>
                          <TableCell>
                            {w.uygun ? (
                              <Badge className="badge-green"><CheckCircle size={12} weight="fill" className="mr-1" /> Uygun</Badge>
                            ) : (
                              <Badge className="badge-red"><Warning size={12} weight="fill" className="mr-1" /> Riskli</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-semibold">{formatMoney(w.yazilabilir_tutar)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <div className="text-xs text-muted-foreground bg-secondary/30 border border-border rounded-md p-3">
                💡 <b>Yazılabilir Tutar</b>: Portföyünüzdeki müşteri çekleri + takasdaki çekler + banka bakiyesi'nden, kendi çekleriniz + krediler + giderler düşüldükten sonra o haftanın sonunda kalan pozitif kümülatif tutar. Bu tutara kadar yeni çek yazabilirsiniz.
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Yeni Ödeme Ekle</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Şirket</Label>
              <Select value={newPay.company_id} onValueChange={(v) => setNewPay({ ...newPay, company_id: v })}>
                <SelectTrigger data-testid="pay-company"><SelectValue placeholder="Seç..." /></SelectTrigger>
                <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vade Tarihi</Label>
              <Input data-testid="pay-vade" type="date" value={newPay.vade_tarihi}
                onChange={(e) => setNewPay({ ...newPay, vade_tarihi: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Açıklama</Label>
              <Input data-testid="pay-baslik" value={newPay.baslik}
                onChange={(e) => setNewPay({ ...newPay, baslik: e.target.value })} placeholder="Örn: Kira, elektrik, taşeron ödemesi" />
            </div>
            <div className="col-span-2">
              <Label>Firma / Taşeron / Ticari Ünvan</Label>
              <Input data-testid="pay-ticari" value={newPay.ticari_unvan}
                onChange={(e) => setNewPay({ ...newPay, ticari_unvan: e.target.value })} />
            </div>
            <div>
              <Label>Ödeme Şekli</Label>
              <Select value={newPay.odeme_sekli} onValueChange={(v) => setNewPay({ ...newPay, odeme_sekli: v })}>
                <SelectTrigger data-testid="pay-sekli"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Nakit","Havale/EFT","Çek","Kredi Kartı","Diğer"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tutar</Label>
              <div className="flex gap-2">
                <MoneyInput data-testid="pay-tutar" value={newPay.tutar}
                  onChange={(n) => setNewPay({ ...newPay, tutar: n })} />
                <Select value={newPay.currency} onValueChange={(v) => setNewPay({ ...newPay, currency: v })}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["TRY","USD","EUR"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Banka</Label>
              <Input value={newPay.banka} onChange={(e) => setNewPay({ ...newPay, banka: e.target.value })} />
            </div>
            <div>
              <Label>IBAN</Label>
              <IbanInput value={newPay.iban} onChange={(e) => setNewPay({ ...newPay, iban: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)}>İptal</Button>
            <Button onClick={savePayment} data-testid="pay-save-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
